import { useEffect, useId, useState, type FormEvent } from 'react';
import type { ChartTextDto, SizeChartDto } from '@ecom/contracts';
import { FilterBar } from '@/components/filters';
import { BilingualField } from '@/components/fields';
import { PlusIcon, SlidersIcon, TrashIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Field, IconButton, Input, TableSkeleton } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date } from '@/lib/format';
import { useSizeChartsStore } from '@/stores/size-charts.store';

/**
 * Size charts (ADR-072) — tables the store writes once and hangs on a
 * category, or on one product, in their own editors. A product shows its own,
 * else its nearest category's, as "Size guide" beside the buy button.
 */
export function SizeChartsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, total, search, loading, error, load, loadMore } = useSizeChartsStore();
  const [editing, setEditing] = useState<SizeChartDto | 'new' | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  const add = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('addSizeChart')}
    </Button>
  );

  return (
    <Page title={t('sizeCharts')} subtitle={t('sizeChartsSubtitle')} actions={add} fill>
      <ListCard>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load(term)} placeholder={t('searchSizeCharts')} total={total ?? undefined} shown={items.length} />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noSizeCharts')} body={t('noSizeChartsHint')} icon={<SlidersIcon />} action={add} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('name')}</Th>
                  <Th>{t('sizeChartShape')}</Th>
                  <Th>{t('sizeChartHangsOn')}</Th>
                  <Th align="end">{t('updatedAt')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((chart) => (
                  <Tr key={chart.id} onClick={() => setEditing(chart)}>
                    <Td>
                      <span className="block font-medium text-ink">{chart.name}</span>
                      <span className="block text-xs text-ink-muted">{chart.title[locale] || chart.title.en || chart.title.ar}</span>
                    </Td>
                    <Td className="text-ink-muted">
                      {t('sizeChartShapeValue', { columns: count(chart.columns.length, locale), rows: count(chart.rows.length, locale) })}
                    </Td>
                    <Td className="text-ink-muted">
                      {chart.categoryCount + chart.productCount === 0
                        ? t('sizeChartUnused')
                        : t('sizeChartUsage', { categories: count(chart.categoryCount, locale), products: count(chart.productCount, locale) })}
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-ink-muted">
                      {date(chart.updatedAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? (
        <ChartSheet key={editing === 'new' ? 'new' : editing.id} chart={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : null}
    </Page>
  );
}

interface Draft {
  name: string;
  title: ChartTextDto;
  columns: ChartTextDto[];
  rows: string[][];
  note: ChartTextDto;
}

function draftOf(chart: SizeChartDto | null): Draft {
  if (!chart) {
    return {
      name: '',
      title: { ar: '', en: '' },
      columns: [
        { ar: 'المقاس', en: 'Size' },
        { ar: '', en: '' },
      ],
      rows: [
        ['S', ''],
        ['M', ''],
        ['L', ''],
      ],
      note: { ar: '', en: '' },
    };
  }
  return { name: chart.name, title: chart.title, columns: chart.columns, rows: chart.rows, note: chart.note ?? { ar: '', en: '' } };
}

const MAX_COLUMNS = 12;
const MAX_ROWS = 40;

/** One chart: its names, then the grid — headings in both languages, a cell per column. */
function ChartSheet({ chart, onDone }: { chart: SizeChartDto | null; onDone: () => void }) {
  const t = useT();
  const formId = useId();
  const { saving, saveError, save, remove, clearSaveError } = useSizeChartsStore();
  const [draft, setDraft] = useState<Draft>(() => draftOf(chart));
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  const setCell = (row: number, column: number, value: string) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((cells, at) => (at === row ? cells.map((cell, index) => (index === column ? value : cell)) : cells)),
    }));
  const setHeading = (column: number, language: 'ar' | 'en', value: string) =>
    setDraft((current) => ({
      ...current,
      columns: current.columns.map((heading, index) => (index === column ? { ...heading, [language]: value } : heading)),
    }));
  const addColumn = () =>
    setDraft((current) => ({ ...current, columns: [...current.columns, { ar: '', en: '' }], rows: current.rows.map((cells) => [...cells, '']) }));
  const removeColumn = (column: number) =>
    setDraft((current) => ({
      ...current,
      columns: current.columns.filter((_, index) => index !== column),
      rows: current.rows.map((cells) => cells.filter((_, index) => index !== column)),
    }));
  const addRow = () => setDraft((current) => ({ ...current, rows: [...current.rows, current.columns.map(() => '')] }));
  const removeRow = (row: number) => setDraft((current) => ({ ...current, rows: current.rows.filter((_, index) => index !== row) }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const ok = await save(chart?.id ?? null, {
      name: draft.name.trim(),
      title: draft.title,
      columns: draft.columns,
      // A row left empty is the editor's spare, not part of the chart.
      rows: draft.rows.filter((cells) => cells.some((cell) => cell.trim())),
      note: draft.note.ar.trim() || draft.note.en.trim() ? draft.note : null,
    });
    if (ok) onDone();
  }

  return (
    <Sheet
      open
      size="lg"
      title={chart ? t('editSizeChart') : t('addSizeChart')}
      subtitle={t('sizeChartEditorHint')}
      onClose={onDone}
      footer={
        <>
          {chart ? (
            <Button variant="ghost" className="me-auto text-danger" onClick={() => setDeleting(true)} disabled={saving}>
              {t('delete')}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-5">
        <Field label={t('sizeChartName')} htmlFor="chart-name" hint={t('sizeChartNameHint')}>
          <Input id="chart-name" maxLength={120} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </Field>
        <BilingualField label={t('sizeChartTitle')} value={draft.title} onChange={(title) => setDraft({ ...draft, title })} limit={120} />

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-medium text-ink">{t('sizeChartGrid')}</legend>
          <p className="text-xs text-ink-muted">{t('sizeChartGridHint')}</p>
          <div className="overflow-x-auto rounded-control border border-line">
            <table className="text-sm">
              <thead>
                <tr className="bg-surface-sunken">
                  {draft.columns.map((heading, column) => (
                    <th key={column} scope="col" className="min-w-36 p-2 align-top font-normal">
                      <div className="flex flex-col gap-1">
                        <Input
                          aria-label={t('sizeChartHeadingAr', { number: count(column + 1, 'ar') })}
                          placeholder="عربي"
                          dir="rtl"
                          value={heading.ar}
                          onChange={(event) => setHeading(column, 'ar', event.target.value)}
                        />
                        <Input
                          aria-label={t('sizeChartHeadingEn', { number: String(column + 1) })}
                          placeholder="English"
                          dir="ltr"
                          value={heading.en}
                          onChange={(event) => setHeading(column, 'en', event.target.value)}
                        />
                        {draft.columns.length > 1 ? (
                          <IconButton label={t('sizeChartRemoveColumn')} onClick={() => removeColumn(column)} className="self-center hover:text-danger">
                            <TrashIcon className="size-4" />
                          </IconButton>
                        ) : null}
                      </div>
                    </th>
                  ))}
                  <th className="w-12" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {draft.rows.map((cells, row) => (
                  <tr key={row} className="border-t border-line">
                    {cells.map((cell, column) => (
                      <td key={column} className="p-2">
                        <Input
                          aria-label={t('sizeChartCell', { row: String(row + 1), column: String(column + 1) })}
                          dir="auto"
                          maxLength={40}
                          value={cell}
                          onChange={(event) => setCell(row, column, event.target.value)}
                        />
                      </td>
                    ))}
                    <td className="p-2">
                      {draft.rows.length > 1 ? (
                        <IconButton label={t('sizeChartRemoveRow')} onClick={() => removeRow(row)} className="hover:text-danger">
                          <TrashIcon className="size-4" />
                        </IconButton>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={addRow} disabled={draft.rows.length >= MAX_ROWS}>
              <PlusIcon className="size-4" />
              {t('sizeChartAddRow')}
            </Button>
            <Button variant="secondary" size="sm" onClick={addColumn} disabled={draft.columns.length >= MAX_COLUMNS}>
              <PlusIcon className="size-4" />
              {t('sizeChartAddColumn')}
            </Button>
          </div>
        </fieldset>

        <BilingualField
          label={t('sizeChartNote')}
          hint={t('sizeChartNoteHint')}
          value={draft.note}
          onChange={(note) => setDraft({ ...draft, note })}
          multiline
          rows={2}
          limit={300}
        />

        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>

      <ConfirmDialog
        open={deleting}
        title={t('deleteSizeChartTitle')}
        body={t('deleteSizeChartBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (chart && (await remove(chart.id))) onDone();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </Sheet>
  );
}
